import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { Button, Typography, Box } from '@altermaind/web/ui';
import { useLanguageSelector } from '@/_shared/components';
import { environment } from '@/_shared/configuration/env';
import { DEFAULT_LANGUAGE, Routes, supportedLanguages, FeatureFlag } from '@/_shared/constants';
import { getEnvironment } from '@/_shared/helpers';
import { useNotification, useFeatureFlags } from '@/_shared/hooks';
import { SupportedLocale } from '@/_shared/types/languages';
import { ActionTypes, AuthResponse } from '../../state';
import { getAuthSettingsCall } from '../../state/api';
import { useOAuth2Localized } from './useOAuth2Localized';

const LANG_STORAGE_KEY = 'app_lang';

const getSavedLanguage = () => {
  const savedLang = localStorage.getItem(LANG_STORAGE_KEY);
  return savedLang || DEFAULT_LANGUAGE;
};

export const LoginCta = () => {
  const t = useTranslations('login.loginForm');
  const prevLangRef = useRef<SupportedLocale['value'] | undefined>(undefined);
  const sharedT = useTranslations();
  const dispatch = useDispatch();
  const { notify } = useNotification();
  const { loginData, tokenData } = getAuthSettingsCall.hook();
  const { changeLanguage } = useLanguageSelector();
  const { isProduction } = getEnvironment();
  const { isFeatureEnabled } = useFeatureFlags();
  const isRegistrationEnabled = isFeatureEnabled(FeatureFlag.REGISTRATION);

  const { watch } = useForm({
    defaultValues: {
      lang: supportedLanguages.find(locale => locale.value === getSavedLanguage()),
    },
  });
  const langField = watch('lang');

  const redirectUri = `${document.location.origin}${Routes.OAuthPopup}`;
  const { getAuthIta, getAuthEn, getAuthEs, getAuthFr } = useOAuth2Localized({
    ...loginData,
    responseType: 'code',
    redirectUri,
    exchangeCodeForTokenQueryFn: async callbackParameters => {
      const { headers, ...tokenParams } = tokenData;
      const body = {
        ...callbackParameters,
        ...tokenParams,
        redirect_uri: redirectUri,
      };

      try {
        const response = await fetch(tokenData.tokenUrl, {
          method: 'POST',
          body: new URLSearchParams(body),
          headers: {
            ...headers,
            Origin: `${document.location.origin}`,
          },
        });
        if (!response.ok) {
          throw new Error();
        }

        const token = await response.json();
        sessionStorage.setItem('accessToken', token.access_token);
        return token;
      } catch {
        throw new Error('Failed');
      }
    },
    // onSuccess has an input parameter which is payload
    onSuccess: (user: AuthResponse) => {
      dispatch({ type: ActionTypes.SET_LOGGED_USER, payload: user });
    },
    // onError has an input parameter which is error
    onError: () => {
      notify(sharedT('errors.genericApiError'), 'error');
    },
  });

  const localizedLoginMap = {
    en: getAuthEn,
    it: getAuthIta,
    fr: getAuthFr,
    es: getAuthEs,
  };

  const goToOnboarding = () => {
    window.location.href = `${environment.VITE_APP_ONBOARDING_URL}`;
  };

  useEffect(() => {
    if (langField?.value && prevLangRef.current !== langField.value) {
      if (prevLangRef.current !== undefined) {
        changeLanguage(langField.value, langField.langCode);
      }
      prevLangRef.current = langField.value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langField]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !isProduction) {
      const originalOpen = window.open;

      // eslint-disable-next-line func-names
      window.open = function (url, target, features) {
        if (url) {
          sessionStorage.setItem('loginUrl', url as string);
        }
        return originalOpen.call(window, url, target, features);
      };
    }
  }, [isProduction]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      paddingTop={'huge' as never}
      paddingBottom={'huge' as never}
      width='70%'
      alignItems='center'
      flex={1}
    >
      <Button
        onClick={localizedLoginMap[langField?.value as keyof typeof localizedLoginMap]}
        mb='m'
        data-testid='login'
      >
        {t('signIn')}
      </Button>
      {isRegistrationEnabled && (
        <>
          <Typography variant='p2/light' mb='xxs' color='grey/light'>
            {t('newUser')}
          </Typography>
          <Button
            onClick={goToOnboarding}
            variant='link/primary'
            style={{
              textDecoration: 'unset',
            }}
            mt='none'
            height='auto'
            data-testid='register'
          >
            {t('signUp')}
          </Button>
        </>
      )}
    </Box>
  );
};
